<template>
    <div class="availability-topnav-content">
        <!-- Row 1: date navigation (left) + controls (right) -->
        <div class="availability-topnav-content__main-row">
            <!-- Left: date navigation -->
            <div class="availability-topnav-content__left">
                <button type="button" class="availability-topnav-content__nav-btn" @click="navigateDate('prev')">
                    <span class="availability-topnav-content__chevron availability-topnav-content__chevron--left"></span>
                </button>

                <div class="availability-topnav-content__date-group">
                    <div class="availability-topnav-content__date-badge">
                        <span class="availability-topnav-content__date-day">{{ dateDay }}</span>
                    </div>
                    <div class="availability-topnav-content__date-info">
                        <span class="availability-topnav-content__date-label">{{ dateDisplayText }}</span>
                        <span class="availability-topnav-content__referral-count">
                            {{ $t('layouts.app.availability_topnav.referral_count', { count: referralCount }) }}
                        </span>
                    </div>
                </div>

                <button type="button" class="availability-topnav-content__nav-btn" @click="navigateDate('next')">
                    <span class="availability-topnav-content__chevron availability-topnav-content__chevron--right"></span>
                </button>
            </div>

            <!-- Right: controls -->
            <div v-if="showLocationSelector || showManageButton || showViewToggle" class="availability-topnav-content__right">
                <!-- Location selector -->
                <location-selector-dropdown v-if="showLocationSelector"></location-selector-dropdown>

                <!-- Actions row -->
                <div v-if="showManageButton || showViewToggle" class="availability-topnav-content__actions">
                    <button
                        v-if="showManageButton"
                        type="button"
                        class="availability-topnav-content__manage-btn"
                        @click="$emit('open-manage')"
                    >
                        <common-icon name="setting" size="semi-small"></common-icon>
                        <span class="availability-topnav-content__manage-label">
                            {{ $t('layouts.app.availability_topnav.manage_availability') }}
                        </span>
                        <span class="availability-topnav-content__manage-label--short">
                            {{ $t('layouts.app.availability_topnav.manage_availability_short') }}
                        </span>
                    </button>

                    <div v-if="showViewToggle" class="availability-topnav-content__view-toggle">
                        <button v-for="v in viewOptions" :key="v" type="button" :class="viewBtnClasses(v)" @click="changeView(v)">
                            {{ $t('layouts.app.availability_topnav.view.' + v) }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Row 2: legend or selected slots preview -->
        <div v-if="!(selectMode && selectedCount > 0)" class="availability-topnav-content__legend">
            <div v-for="item in legendItems" :key="item.key" class="availability-topnav-content__legend-item">
                <div
                    :class="['availability-topnav-content__legend-dot', 'availability-topnav-content__legend-dot--' + item.key]"
                ></div>
                <span class="availability-topnav-content__legend-text">
                    {{ $t('layouts.app.availability_topnav.legend.' + item.i18nKey) }}
                </span>
            </div>
        </div>

        <div v-else class="availability-topnav-content__selected-panel">
            <div class="availability-topnav-content__selected-header">
                <span class="availability-topnav-content__selected-title">
                    {{ $t('layouts.app.availability_topnav.selected_slots_title', { count: selectedCount, max: maxSlots }) }}
                </span>
                <button type="button" class="availability-topnav-content__clear-all" @click="CLEAR_SELECTED">
                    {{ $t('layouts.app.availability_topnav.selected_slots_clear_all') }}
                </button>
            </div>
            <div class="availability-topnav-content__selected-cards">
                <div v-for="n in maxSlots" :key="n" :class="selectedCardsClasses[n - 1]">
                    <template v-if="selectedSlots[n - 1]">
                        <div class="availability-topnav-content__selected-card-info">
                            <span class="availability-topnav-content__selected-card-time">
                                {{ selectedSlots[n - 1].startTime }}
                            </span>
                            <span class="availability-topnav-content__selected-card-date">
                                {{ formatSlotDate(selectedSlots[n - 1]) }}
                            </span>
                        </div>
                        <button
                            type="button"
                            class="availability-topnav-content__selected-card-remove"
                            @click="TOGGLE_SLOT(selectedSlots[n - 1])"
                        >
                            &#215;
                        </button>
                    </template>
                    <span v-else class="availability-topnav-content__selected-card-placeholder">
                        {{ $t('layouts.app.availability_topnav.selected_slot_placeholder', { index: n }) }}
                    </span>
                </div>
            </div>
        </div>

        <div class="availability-topnav-content__divider"></div>

        <!-- Toast notification (fixed, top-right) -->
        <transition name="avail-toast">
            <div v-if="toastVisible" class="availability-topnav-content__toast">
                <span class="availability-topnav-content__toast-icon">
                    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                        <circle cx="10" cy="10" r="9" fill="#111827" />
                        <path
                            d="M6.2 10.2l2.5 2.5 5.2-5.1"
                            stroke="#FFFFFF"
                            stroke-width="1.8"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </span>
                <span class="availability-topnav-content__toast-text">{{ toastMessage }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import { mapGetters, mapMutations } from 'vuex'
    import LocationSelectorDropdown from './LocationSelectorDropdown'

    export default {
        name: 'availability-topnav-content',

        components: {
            LocationSelectorDropdown,
        },

        props: {
            currentDate: { type: Date, default: () => new Date() },
            view: { type: String, default: 'monthly', validator: (v) => ['daily', 'weekly', 'monthly'].includes(v) },
            onDateChange: { type: Function, default: () => {} },
            onViewChange: { type: Function, default: () => {} },
            showLocationSelector: { type: Boolean, default: true },
            showManageButton: { type: Boolean, default: true },
            showViewToggle: { type: Boolean, default: true },
            maxSlots: { type: Number, default: 3 },
        },

        data() {
            return {
                viewOptions: ['daily', 'weekly', 'monthly'],
                legendItems: [
                    { key: 'confirmed', i18nKey: 'confirmed' },
                    { key: 'pending', i18nKey: 'pending' },
                    { key: 'cancelled', i18nKey: 'cancelled_no_show' },
                    { key: 'available', i18nKey: 'available' },
                ],
                toastVisible: false,
                toastMessage: '',
                toastTimer: null,
            }
        },

        computed: {
            ...mapGetters('availability', ['referralCount', 'selectMode', 'selectedSlots', 'selectedCount']),
            selectedCardsClasses() {
                return Array.from({ length: this.maxSlots }, (_, index) => [
                    'availability-topnav-content__selected-card',
                    this.selectedSlots[index]
                        ? 'availability-topnav-content__selected-card--filled'
                        : 'availability-topnav-content__selected-card--empty',
                ])
            },
            dateDay() {
                if (this.view === 'weekly') {
                    const start = new Date(this.currentDate)
                    start.setDate(this.currentDate.getDate() - this.currentDate.getDay())
                    return start.getDate()
                }
                return this.currentDate.getDate()
            },
            dateDisplayText() {
                if (this.view === 'daily') {
                    return this.currentDate.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                    })
                }
                if (this.view === 'weekly') {
                    const start = new Date(this.currentDate)
                    start.setDate(this.currentDate.getDate() - this.currentDate.getDay())
                    const end = new Date(start)
                    end.setDate(start.getDate() + 6)
                    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    return `${startStr} – ${endStr}`
                }
                return this.currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
            },
        },

        methods: {
            ...mapMutations('availability', ['CLEAR_SELECTED', 'TOGGLE_SLOT']),
            showToast(message) {
                if (this.toastTimer) clearTimeout(this.toastTimer)
                this.toastMessage = message
                this.toastVisible = true
                this.toastTimer = setTimeout(() => {
                    this.toastVisible = false
                }, 3500)
            },
            formatSlotDate(slot) {
                return dayjs(slot.date).format('ddd, MMM D, YYYY')
            },
            viewBtnClasses(v) {
                return {
                    'availability-topnav-content__view-btn': true,
                    'availability-topnav-content__view-btn--active': this.view === v,
                }
            },
            navigateDate(direction) {
                const d = new Date(this.currentDate)
                if (this.view === 'daily') {
                    d.setDate(d.getDate() + (direction === 'next' ? 1 : -1))
                } else if (this.view === 'weekly') {
                    d.setDate(d.getDate() + (direction === 'next' ? 7 : -7))
                } else {
                    d.setMonth(d.getMonth() + (direction === 'next' ? 1 : -1))
                }
                this.onDateChange(d)
            },
            changeView(v) {
                this.onViewChange(v)
            },
        },

        watch: {
            selectedCount(newVal, oldVal) {
                if (this.selectMode && newVal > oldVal && this.selectedSlots[newVal - 1]) {
                    const slot = this.selectedSlots[newVal - 1]
                    const dateLabel = dayjs(slot.date).format('ddd, MMM D, YYYY')
                    this.showToast(
                        this.$t('layouts.app.availability_topnav.toast.slot_selected', {
                            time: slot.startTime,
                            date: dateLabel,
                        }),
                    )
                }
            },
        },
    }
</script>

<style scoped>
    /* ─── Wrapper ────────────────────────────────────────────── */
    .availability-topnav-content {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    /* Row 1 ─────────────────────────────────────────────────── */
    .availability-topnav-content__main-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        min-width: 0;
        padding: 16px 0 12px 0;
    }

    /* Left: date nav */
    .availability-topnav-content__left {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }

    .availability-topnav-content__nav-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background: #ffffff;
        border: 1px solid #dbe2ea;
        border-radius: 6px;
        cursor: pointer;
        padding: 0;
        flex-shrink: 0;
        transition: background 0.15s;
    }

    .availability-topnav-content__nav-btn:hover {
        background: #f9fafb;
    }

    /* CSS chevron arrows for prev/next */
    .availability-topnav-content__chevron {
        display: inline-block;
        width: 7px;
        height: 7px;
        border-top: 1.25px solid #4b5563;
        border-right: 1.25px solid #4b5563;
        flex-shrink: 0;
    }

    .availability-topnav-content__chevron--left {
        transform: rotate(-135deg) translate(-1px, 1px);
    }

    .availability-topnav-content__chevron--right {
        transform: rotate(45deg) translate(-1px, 1px);
    }

    .availability-topnav-content__date-group {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .availability-topnav-content__date-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: #22d3ee;
        border-radius: 8px;
        flex-shrink: 0;
    }

    .availability-topnav-content__date-day {
        font-size: 20px;
        font-weight: 500;
        color: #ffffff;
        line-height: 1;
    }

    .availability-topnav-content__date-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    .availability-topnav-content__date-label {
        font-size: 18px;
        font-weight: 500;
        line-height: 1.25;
        color: #0f172a;
        white-space: nowrap;
    }

    .availability-topnav-content__referral-count {
        font-size: 12px;
        font-weight: 400;
        line-height: 1.2;
        color: #94a3b8;
    }

    /* Right: controls */
    .availability-topnav-content__right {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
    }

    /* Actions */
    .availability-topnav-content__actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    /* Manage Availability button */
    .availability-topnav-content__manage-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 38px;
        padding: 0 14px;
        background: #ffffff;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        color: #374151;
        cursor: pointer;
        white-space: nowrap;
        transition: background 0.15s;
    }

    .availability-topnav-content__manage-btn:hover {
        background: #f9fafb;
    }

    .availability-topnav-content__manage-label--short {
        display: none;
    }

    /* View toggle */
    .availability-topnav-content__view-toggle {
        display: flex;
        align-items: center;
        background: #f3f4f6;
        border-radius: 8px;
        padding: 3px;
        gap: 2px;
    }

    .availability-topnav-content__view-btn {
        height: 32px;
        padding: 0 13px;
        background: transparent;
        border: none;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.2;
        color: #6b7280;
        cursor: pointer;
        transition: background 0.15s, color 0.15s;
        white-space: nowrap;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
    }

    .availability-topnav-content__view-btn:hover {
        color: #111827;
    }

    .availability-topnav-content__view-btn--active {
        background: #111827;
        color: #ffffff;
        font-weight: 500;
    }

    .availability-topnav-content__view-btn--active:hover {
        background: #1f2937;
        color: #ffffff;
    }

    /* ─── Row 2: legend ──────────────────────────────────────── */
    .availability-topnav-content__legend {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px 24px;
        padding: 12px 0;
    }

    .availability-topnav-content__legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .availability-topnav-content__legend-dot {
        width: 16px;
        height: 16px;
        border-radius: 4px;
        flex-shrink: 0;
    }

    .availability-topnav-content__legend-dot--confirmed {
        background: #22c55e;
    }
    .availability-topnav-content__legend-dot--pending {
        background: #f97316;
    }
    .availability-topnav-content__legend-dot--cancelled {
        background: #fee2e2;
        border: 2px solid #ef4444;
    }
    .availability-topnav-content__legend-dot--available {
        background: #e5e7eb;
        border: 1px solid #d1d5db;
    }

    .availability-topnav-content__legend-text {
        font-size: 13px;
        font-weight: 400;
        color: #6b7280;
        white-space: nowrap;
    }

    /* ─── Divider ────────────────────────────────────────────── */
    .availability-topnav-content__divider {
        height: 1px;
        background: #e5e7eb;
        margin: 12px -24px 0 -24px;
    }

    /* ─── Selected slots panel ───────────────────────────────── */
    .availability-topnav-content__selected-panel {
        background: #edf4ff;
        border: 1px solid #bfd7ff;
        border-radius: 12px;
        padding: 14px;
        margin: 8px 0 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .availability-topnav-content__selected-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .availability-topnav-content__selected-title {
        font-size: 14px;
        font-weight: 500;
        line-height: 1.25;
        color: #0f172a;
    }

    .availability-topnav-content__clear-all {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        font-size: 13px;
        font-weight: 500;
        color: #2563eb;
        cursor: pointer;
        padding: 3px 8px;
        border-radius: 6px;
        line-height: 1.2;
        transition: background 0.15s ease, color 0.15s ease;
    }

    .availability-topnav-content__clear-all:hover {
        background: #dbeafe;
        color: #1d4ed8;
    }

    .availability-topnav-content__selected-cards {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 10px;
    }

    .availability-topnav-content__selected-card {
        min-width: 0;
        border-radius: 11px;
        padding: 11px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 74px;
    }

    .availability-topnav-content__selected-card--filled {
        background: #f8fbff;
        border: 1px solid #9dc4ff;
        box-shadow: none;
    }

    .availability-topnav-content__selected-card--empty {
        background: rgba(255, 255, 255, 0.32);
        border: 1px dashed #bfd1ea;
    }

    .availability-topnav-content__selected-card-info {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }

    .availability-topnav-content__selected-card-time {
        font-size: 14px;
        font-weight: 500;
        line-height: 1.2;
        color: #0f172a;
    }

    .availability-topnav-content__selected-card-date {
        font-size: 11px;
        line-height: 1.2;
        color: #64748b;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .availability-topnav-content__selected-card-remove {
        width: 26px;
        height: 26px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        font-size: 20px;
        color: #9ca3af;
        cursor: pointer;
        padding: 0;
        border-radius: 6px;
        line-height: 1;
        flex-shrink: 0;
        transition: background 0.15s ease, color 0.15s ease;
    }

    .availability-topnav-content__selected-card-remove:hover {
        background: #fee2e2;
        color: #ef4444;
    }

    .availability-topnav-content__selected-card-placeholder {
        font-size: 13px;
        color: #94a3b8;
        font-weight: 400;
        line-height: 1.2;
        margin: auto;
    }

    /* ─── Row 3: select slots ────────────────────────────────── */

    /* ─── Toast ──────────────────────────────────────────────── */
    .availability-topnav-content__toast {
        position: fixed;
        top: 34px;
        right: 24px;
        z-index: 9999;
        display: flex;
        align-items: center;
        gap: 12px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 14px;
        box-shadow: 0 8px 20px rgba(17, 24, 39, 0.12);
        padding: 16px 20px;
        max-width: 520px;
        min-width: 360px;
    }

    .availability-topnav-content__toast-icon {
        flex-shrink: 0;
    }

    .availability-topnav-content__toast-text {
        font-size: 13px;
        color: #374151;
        line-height: 1.35;
    }

    .avail-toast-enter-active,
    .avail-toast-leave-active {
        transition: opacity 0.25s ease, transform 0.25s ease;
    }

    .avail-toast-enter,
    .avail-toast-leave-to {
        opacity: 0;
        transform: translateY(-6px);
    }

    /* ─── Responsive ─────────────────────────────────────────── */
    /* Tablet: 768px and up */
    @media (max-width: 768px) {
        .availability-topnav-content {
            gap: 12px;
        }

        .availability-topnav-content__main-row {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
        }

        .availability-topnav-content__left {
            width: 100%;
            order: 1;
        }

        .availability-topnav-content__date-label {
            font-size: 16px;
        }

        .availability-topnav-content__right {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            order: 2;
        }

        .availability-topnav-content__actions {
            flex-direction: column;
            gap: 12px;
        }

        .availability-topnav-content__manage-btn {
            width: 100%;
            justify-content: center;
            height: 40px;
            font-size: 13px;
        }

        .availability-topnav-content__view-toggle {
            width: 100%;
        }

        .availability-topnav-content__view-btn {
            flex: 1;
            font-size: 13px;
            height: 36px;
        }

        .availability-topnav-content__divider {
            margin: 4px -24px 0;
        }

        .availability-topnav-content__legend {
            gap: 8px 16px;
        }

        .availability-topnav-content__legend-text {
            font-size: 12px;
        }
    }

    /* Mobile Large: 425px */
    @media (max-width: 425px) {
        .availability-topnav-content {
            gap: 10px;
        }

        .availability-topnav-content__main-row {
            gap: 10px;
        }

        .availability-topnav-content__left {
            gap: 6px;
        }

        .availability-topnav-content__nav-btn {
            width: 28px;
            height: 28px;
            font-size: 12px;
        }

        .availability-topnav-content__date-badge {
            width: 36px;
            height: 36px;
        }

        .availability-topnav-content__date-day {
            font-size: 18px;
        }

        .availability-topnav-content__date-label {
            font-size: 14px;
        }

        .availability-topnav-content__referral-count {
            font-size: 12px;
        }

        .availability-topnav-content__right {
            gap: 10px;
        }

        .availability-topnav-content__actions {
            gap: 10px;
        }

        .availability-topnav-content__manage-btn {
            height: 38px;
            padding: 0 12px;
            font-size: 12px;
        }

        .availability-topnav-content__manage-label {
            display: none;
        }

        .availability-topnav-content__manage-label--short {
            display: inline;
        }

        .availability-topnav-content__view-btn {
            padding: 0 10px;
            font-size: 12px;
            height: 32px;
        }

        .availability-topnav-content__divider {
            margin: 4px -24px 0;
        }

        .availability-topnav-content__legend {
            gap: 6px 12px;
            justify-content: flex-start;
        }

        .availability-topnav-content__legend-item {
            gap: 6px;
            font-size: 11px;
        }

        .availability-topnav-content__legend-dot {
            width: 12px;
            height: 12px;
        }

        .availability-topnav-content__legend-text {
            font-size: 11px;
        }

        .availability-topnav-content__selected-cards {
            grid-template-columns: 1fr;
        }

        .availability-topnav-content__toast {
            top: 16px;
            right: 12px;
            left: 12px;
            min-width: 0;
            max-width: none;
        }

        .availability-topnav-content__selected-card-time {
            font-size: 16px;
        }
    }

    /* Mobile Medium: 375px */
    @media (max-width: 375px) {
        .availability-topnav-content {
            gap: 9px;
        }

        .availability-topnav-content__main-row {
            gap: 9px;
        }

        .availability-topnav-content__left {
            gap: 5px;
        }

        .availability-topnav-content__date-group {
            gap: 8px;
        }

        .availability-topnav-content__date-badge {
            width: 34px;
            height: 34px;
        }

        .availability-topnav-content__date-day {
            font-size: 16px;
        }

        .availability-topnav-content__date-info {
            gap: 1px;
        }

        .availability-topnav-content__date-label {
            font-size: 13px;
            line-height: 1.2;
        }

        .availability-topnav-content__referral-count {
            font-size: 11px;
        }

        .availability-topnav-content__right {
            gap: 9px;
        }

        .availability-topnav-content__actions {
            gap: 9px;
            width: 100%;
        }

        .availability-topnav-content__manage-btn {
            height: 36px;
            padding: 0 10px;
            font-size: 11px;
            flex: 1;
            min-width: auto;
        }

        .availability-topnav-content__view-toggle {
            padding: 3px;
            gap: 1px;
            flex: 1;
        }

        .availability-topnav-content__view-btn {
            padding: 0 8px;
            font-size: 11px;
            height: 30px;
        }

        .availability-topnav-content__legend {
            gap: 5px 10px;
        }

        .availability-topnav-content__legend-item {
            gap: 5px;
        }

        .availability-topnav-content__legend-dot {
            width: 11px;
            height: 11px;
        }

        .availability-topnav-content__legend-text {
            font-size: 10px;
        }
    }

    /* Mobile Small: 320px */
    @media (max-width: 320px) {
        .availability-topnav-content {
            gap: 8px;
        }

        .availability-topnav-content__main-row {
            gap: 8px;
        }

        .availability-topnav-content__left {
            gap: 4px;
            width: 100%;
        }

        .availability-topnav-content__nav-btn {
            width: 26px;
            height: 26px;
        }

        .availability-topnav-content__date-group {
            gap: 6px;
            flex: 1;
        }

        .availability-topnav-content__date-badge {
            width: 32px;
            height: 32px;
            flex-shrink: 0;
        }

        .availability-topnav-content__date-day {
            font-size: 14px;
        }

        .availability-topnav-content__date-label {
            font-size: 12px;
            white-space: normal;
            line-height: 1.1;
        }

        .availability-topnav-content__referral-count {
            font-size: 10px;
        }

        .availability-topnav-content__right {
            width: 100%;
            flex-direction: column;
            gap: 8px;
        }

        .availability-topnav-content__actions {
            width: 100%;
            gap: 8px;
        }

        .availability-topnav-content__manage-btn {
            width: 100%;
            height: 34px;
            padding: 0 8px;
            font-size: 10px;
            min-width: auto;
        }

        .availability-topnav-content__date-badge {
            height: 32px;
            min-height: 32px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }

        .availability-topnav-content__left {
            width: 100%;
            margin-bottom: 8px;
        }

        .availability-topnav-content__view-toggle {
            width: 100%;
            padding: 2px;
            gap: 1px;
        }

        .availability-topnav-content__view-btn {
            padding: 0 6px;
            font-size: 10px;
            height: 28px;
        }

        .availability-topnav-content__divider {
            margin: 4px -24px 0;
        }

        .availability-topnav-content__legend {
            gap: 4px 8px;
            flex-wrap: wrap;
        }

        .availability-topnav-content__legend-item {
            gap: 4px;
            font-size: 9px;
            flex: 0 1 48%;
        }

        .availability-topnav-content__legend-dot {
            width: 10px;
            height: 10px;
            flex-shrink: 0;
        }

        .availability-topnav-content__legend-text {
            font-size: 9px;
            line-height: 1.1;
        }
    }
</style>
